import { useEffect, useMemo, useState } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import { useFundsQuery } from "../../lib/api/reference";
import { uniqueProgrammeGroups } from "../../lib/group-settings-units";
import {
  useCreateSettingsBankAccountMutation,
  useCreateSettingsOpeningBalanceMutation,
  useCreateSettingsUnitMutation,
  useSettingsBankAccountsQuery,
  useSettingsOpeningBalancesQuery,
  useSettingsUnitsQuery,
  useUpdateSettingsUnitMutation,
  type UnitAdminDto,
} from "../../lib/api/settings";
import { formatMoney } from "../../lib/format/money";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { EmptyState } from "../../components/ui/EmptyState";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Modal } from "../../components/ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";

type ReferenceSection = "programmes" | "banks" | "opening";

const SECTIONS: { id: ReferenceSection; label: string }[] = [
  { id: "programmes", label: "Programmes" },
  { id: "banks", label: "Bank accounts" },
  { id: "opening", label: "Opening balances" },
];

function UnitEditForm({ unit, onClose }: { unit: UnitAdminDto; onClose: () => void }) {
  const updateUnit = useUpdateSettingsUnitMutation();
  const [name, setName] = useState(unit.name);
  const [annualBudget, setAnnualBudget] = useState(unit.annualBudget);
  const [active, setActive] = useState(unit.active);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      await updateUnit.mutateAsync({
        id: unit.id,
        name,
        annualBudget: Number.parseFloat(annualBudget).toFixed(2),
        active,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not update programme.");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${unit.code}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={updateUnit.isPending} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      {error ? (
        <p className="edlab-error mb-3">
          {error}
        </p>
      ) : null}
      <div className="grid gap-3">
        <label className="grid gap-1 text-sm">
          Name
          <input
            className="edlab-input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Annual budget (PKR)
          <input
            type="number"
            step="0.01"
            className="edlab-input"
            value={annualBudget}
            onChange={(e) => {
              setAnnualBudget(e.target.value);
            }}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => {
              setActive(e.target.checked);
            }}
          />
          Active
        </label>
      </div>
    </Modal>
  );
}

export function SystemReferencePanel() {
  const [section, setSection] = useState<ReferenceSection>("programmes");
  const [error, setError] = useState<string | null>(null);
  const [editUnit, setEditUnit] = useState<UnitAdminDto | null>(null);

  const units = useSettingsUnitsQuery(true);
  const banks = useSettingsBankAccountsQuery(section === "banks" || section === "opening");
  const openingBalances = useSettingsOpeningBalancesQuery(section === "opening");
  const funds = useFundsQuery(section === "opening");

  const createBankAccount = useCreateSettingsBankAccountMutation();
  const createUnit = useCreateSettingsUnitMutation();
  const createOpeningBalance = useCreateSettingsOpeningBalanceMutation();

  const [bankName, setBankName] = useState("");
  const [bankKind, setBankKind] = useState<"bank" | "cash">("bank");
  const [unitGroupId, setUnitGroupId] = useState("");
  const [unitName, setUnitName] = useState("");
  const [unitCode, setUnitCode] = useState("");
  const [unitBudget, setUnitBudget] = useState("");
  const [obBankId, setObBankId] = useState("");
  const [obFundId, setObFundId] = useState("");
  const [obUnitId, setObUnitId] = useState("");
  const [obAmount, setObAmount] = useState("");
  const { startsOn } = useFiscalDefaults();
  const [obDate, setObDate] = useState("");

  const groupOptions = useMemo(
    () => uniqueProgrammeGroups(units.data?.units),
    [units.data?.units],
  );

  const flatUnits = useMemo(
    () => (units.data?.units ?? []).filter((unit) => unit.active),
    [units.data?.units],
  );

  const selectedGroupId = unitGroupId || groupOptions[0]?.id || "";
  const selectedObBankId = obBankId || banks.data?.bankAccounts[0]?.id || "";
  const selectedObFundId = obFundId || funds.data?.funds[0]?.id || "";
  const selectedObUnitId = obUnitId || flatUnits[0]?.id || "";

  useEffect(() => {
    if (startsOn && !obDate) {
      setObDate(startsOn);
    }
  }, [startsOn, obDate]);

  async function runAction(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Action failed.");
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((item) => (
          <Button
            key={item.id}
            variant={section === item.id ? "primary" : "secondary"}
            onClick={() => {
              setSection(item.id);
              setError(null);
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {error ? (
        <p className="edlab-error">
          {error}
        </p>
      ) : null}

      {section === "programmes" ? (
        <div className="grid gap-4">
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Add programme unit</h3>
            <div className="grid gap-3 md:grid-cols-5">
              <label className="grid gap-1 text-sm">
                Group
                <select
                  className="edlab-input"
                  value={selectedGroupId}
                  onChange={(e) => {
                    setUnitGroupId(e.target.value);
                  }}
                >
                  {groupOptions.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Name
                <input
                  className="edlab-input"
                  value={unitName}
                  onChange={(e) => {
                    setUnitName(e.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                Code
                <input
                  className="edlab-input"
                  value={unitCode}
                  onChange={(e) => {
                    setUnitCode(e.target.value);
                  }}
                  placeholder="e.g. ha"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Annual budget
                <input
                  type="number"
                  step="0.01"
                  className="edlab-input"
                  value={unitBudget}
                  onChange={(e) => {
                    setUnitBudget(e.target.value);
                  }}
                />
              </label>
              <div className="flex items-end">
                <Button
                  disabled={createUnit.isPending || !unitName || !unitCode || !unitBudget || !selectedGroupId}
                  onClick={() =>
                    void runAction(async () => {
                      await createUnit.mutateAsync({
                        groupId: selectedGroupId,
                        name: unitName,
                        code: unitCode,
                        annualBudget: Number.parseFloat(unitBudget).toFixed(2),
                      });
                      setUnitName("");
                      setUnitCode("");
                      setUnitBudget("");
                    })
                  }
                >
                  Add unit
                </Button>
              </div>
            </div>
          </Card>
          <Card>
            {units.isLoading ? (
              <LoadingSpinner label="Loading programmes…" />
            ) : (units.data?.units.length ?? 0) === 0 ? (
              <EmptyState title="No programmes" description="Add a programme unit above." />
            ) : (
              <Table caption="Programme units">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Code</TableHeaderCell>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Group</TableHeaderCell>
                    <TableHeaderCell>Budget</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {units.data?.units.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell>{unit.code}</TableCell>
                      <TableCell>{unit.name}</TableCell>
                      <TableCell>{unit.groupName}</TableCell>
                      <TableCell>{formatMoney(unit.annualBudget)}</TableCell>
                      <TableCell>{unit.active ? "Active" : "Inactive"}</TableCell>
                      <TableCell>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setEditUnit(unit);
                          }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      ) : null}

      {section === "banks" ? (
        <div className="grid gap-4">
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Add bank or cash account</h3>
            <div className="flex flex-wrap items-end gap-3">
              <label className="grid gap-1 text-sm">
                Name
                <input
                  className="edlab-input"
                  value={bankName}
                  onChange={(e) => {
                    setBankName(e.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                Kind
                <select
                  className="edlab-input"
                  value={bankKind}
                  onChange={(e) => {
                    setBankKind(e.target.value as "bank" | "cash");
                  }}
                >
                  <option value="bank">Bank</option>
                  <option value="cash">Cash</option>
                </select>
              </label>
              <Button
                disabled={createBankAccount.isPending || !bankName}
                onClick={() =>
                  void runAction(async () => {
                    await createBankAccount.mutateAsync({ name: bankName, kind: bankKind });
                    setBankName("");
                  })
                }
              >
                Add account
              </Button>
            </div>
          </Card>
          <Card>
            {banks.isLoading ? (
              <LoadingSpinner label="Loading bank accounts…" />
            ) : (
              <Table caption="Bank accounts">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Kind</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {banks.data?.bankAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>{account.name}</TableCell>
                      <TableCell>{account.kind}</TableCell>
                      <TableCell>{account.active ? "Active" : "Inactive"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      ) : null}

      {section === "opening" ? (
        <div className="grid gap-4">
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Set opening balance</h3>
            <p className="mb-3 text-sm text-edlab-muted">
              Updates an existing bank + fund + programme combination, or creates a new one.
            </p>
            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
              <label className="grid gap-1 text-sm">
                Bank account
                <select
                  className="edlab-input"
                  value={selectedObBankId}
                  onChange={(e) => {
                    setObBankId(e.target.value);
                  }}
                >
                  {(banks.data?.bankAccounts ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Fund
                <select
                  className="edlab-input"
                  value={selectedObFundId}
                  onChange={(e) => {
                    setObFundId(e.target.value);
                  }}
                >
                  {(funds.data?.funds ?? []).map((fund) => (
                    <option key={fund.id} value={fund.id}>
                      {fund.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Programme
                <select
                  className="edlab-input"
                  value={selectedObUnitId}
                  onChange={(e) => {
                    setObUnitId(e.target.value);
                  }}
                >
                  {flatUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.code} — {unit.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Amount (PKR)
                <input
                  type="number"
                  step="0.01"
                  className="edlab-input"
                  value={obAmount}
                  onChange={(e) => {
                    setObAmount(e.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                Effective date
                <input
                  type="date"
                  className="edlab-input"
                  value={obDate}
                  onChange={(e) => {
                    setObDate(e.target.value);
                  }}
                />
              </label>
              <div className="flex items-end">
                <Button
                  disabled={
                    createOpeningBalance.isPending ||
                    !obAmount ||
                    !selectedObBankId ||
                    !selectedObFundId ||
                    !selectedObUnitId
                  }
                  onClick={() =>
                    void runAction(async () => {
                      await createOpeningBalance.mutateAsync({
                        bankAccountId: selectedObBankId,
                        fundId: selectedObFundId,
                        unitId: selectedObUnitId,
                        amount: Number.parseFloat(obAmount).toFixed(2),
                        effectiveDate: obDate,
                      });
                      setObAmount("");
                    })
                  }
                >
                  Save balance
                </Button>
              </div>
            </div>
          </Card>
          <Card>
            {openingBalances.isLoading ? (
              <LoadingSpinner label="Loading opening balances…" />
            ) : (openingBalances.data?.openingBalances.length ?? 0) === 0 ? (
              <EmptyState
                title="No opening balances"
                description="Set reviewed opening balances before go-live."
              />
            ) : (
              <Table caption="Opening balances">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Programme</TableHeaderCell>
                    <TableHeaderCell>Fund</TableHeaderCell>
                    <TableHeaderCell>Bank account</TableHeaderCell>
                    <TableHeaderCell>Amount</TableHeaderCell>
                    <TableHeaderCell>Effective</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openingBalances.data?.openingBalances.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.unitCode} — {row.unitName}
                      </TableCell>
                      <TableCell>{row.fundName}</TableCell>
                      <TableCell>{row.bankAccountName}</TableCell>
                      <TableCell>{formatMoney(row.amount)}</TableCell>
                      <TableCell>{row.effectiveDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      ) : null}

      {editUnit ? (
        <UnitEditForm
          key={editUnit.id}
          unit={editUnit}
          onClose={() => {
            setEditUnit(null);
          }}
        />
      ) : null}
    </div>
  );
}
